import React, { useEffect, useState, useMemo } from 'react';
import { db } from '../Services/Firebase/firebaseConfigOuvidoria';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Typography, CircularProgress, Collapse, IconButton, Select,
    MenuItem, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, Button, Grid, TextField, TablePagination, Chip,
    InputAdornment, FormControl, InputLabel, Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import SearchIcon from '@mui/icons-material/Search';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';

const NAVY = '#011838';
const BLUE = '#003E87';
const GOLD = '#FDC500';

const EMPTY_FILTERS = { search: '', status: '', tipo: '', dataInicio: '', dataFim: '' };

// Parses DD/MM/YYYY strings stored in Firestore
const parseCriacao = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length === 3) {
        const [day, month, year] = parts;
        return new Date(`${year}-${month}-${day}`);
    }
    const d = new Date(dateStr);
    return isNaN(d) ? null : d;
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = parseCriacao(dateStr);
    if (!d || isNaN(d)) return dateStr;
    return d.toLocaleDateString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
    });
};

const StatusChip = ({ status }) => {
    const isCompleted = status === 'Concluído';
    const isPendente = status === 'Pendente';
    const bgcolor = isCompleted ? '#E8F5E9' : isPendente ? '#FFF3E0' : '#FFF8E1';
    const color = isCompleted ? '#2E7D32' : isPendente ? '#E65100' : '#B45309';
    const border = isCompleted ? '#A5D6A7' : isPendente ? '#FFCC80' : '#FCD34D';
    return (
        <Chip
            label={status || '-'}
            size="small"
            sx={{
                bgcolor,
                color,
                fontWeight: 600,
                fontSize: 11,
                height: 22,
                border: `1px solid ${border}`,
            }}
        />
    );
};

const SortIcon = ({ column, sortColumn, sortDirection }) => {
    if (sortColumn !== column) {
        return <UnfoldMoreIcon sx={{ fontSize: 15, verticalAlign: 'middle', ml: 0.5, opacity: 0.4 }} />;
    }
    return sortDirection === 'asc'
        ? <ArrowUpwardIcon sx={{ fontSize: 15, verticalAlign: 'middle', ml: 0.5, color: GOLD }} />
        : <ArrowDownwardIcon sx={{ fontSize: 15, verticalAlign: 'middle', ml: 0.5, color: GOLD }} />;
};

const OuvidoriaRH = () => {
    const [dados, setDados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [open, setOpen] = useState({});
    const [sortDirection, setSortDirection] = useState('desc');
    const [sortColumn, setSortColumn] = useState('criacao');
    const [statusTemp, setStatusTemp] = useState({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        const fetchDados = async () => {
            setLoading(true);
            try {
                const querySnapshot = await getDocs(collection(db, 'ouvidoriarh'));
                const dadosArray = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
                setDados(dadosArray);
            } catch (error) {
                console.error('Erro ao buscar dados:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchDados();
    }, []);

    const tiposDisponiveis = useMemo(() =>
        [...new Set(dados.map(d => d.tipo).filter(Boolean))].sort(),
    [dados]);

    const statusPriority = (s) => s === 'Pendente' ? 0 : 1;

    const sortedAndFiltered = useMemo(() => {
        const sorted = [...dados].sort((a, b) => {
            // Always surface Pendente entries first when sorting by date (default view)
            if (sortColumn === 'criacao') {
                const pDiff = statusPriority(a.status) - statusPriority(b.status);
                if (pDiff !== 0) return pDiff;
            }
            const aVal = sortColumn === 'criacao'
                ? (parseCriacao(a.criacao) || new Date(0))
                : (a[sortColumn] || '');
            const bVal = sortColumn === 'criacao'
                ? (parseCriacao(b.criacao) || new Date(0))
                : (b[sortColumn] || '');
            const dir = sortDirection === 'asc' ? 1 : -1;
            return aVal > bVal ? dir : aVal < bVal ? -dir : 0;
        });

        const term = filters.search.toLowerCase();
        return sorted.filter(dado => {
            const matchSearch = !filters.search ||
                (dado.nome || '').toLowerCase().includes(term) ||
                (dado.email || '').toLowerCase().includes(term) ||
                (dado.tipo || '').toLowerCase().includes(term) ||
                (dado.status || '').toLowerCase().includes(term) ||
                String(dado.numeroRequisicao || '').includes(filters.search);
            const matchStatus = !filters.status || dado.status === filters.status;
            const matchTipo = !filters.tipo || dado.tipo === filters.tipo;
            const dataReg = parseCriacao(dado.criacao);
            const dataInicio = filters.dataInicio ? new Date(filters.dataInicio) : null;
            const dataFim = filters.dataFim ? new Date(filters.dataFim + 'T23:59:59') : null;
            const matchDataInicio = !dataInicio || (dataReg && dataReg >= dataInicio);
            const matchDataFim = !dataFim || (dataReg && dataReg <= dataFim);
            return matchSearch && matchStatus && matchTipo && matchDataInicio && matchDataFim;
        });
    }, [dados, sortColumn, sortDirection, filters]);

    const paginatedData = useMemo(() =>
        sortedAndFiltered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedAndFiltered, page, rowsPerPage]);

    const handleFilterChange = (field) => (e) => {
        setFilters(prev => ({ ...prev, [field]: e.target.value }));
        setPage(0);
    };

    const handleSort = (column) => {
        setSortDirection(prev => sortColumn === column && prev === 'asc' ? 'desc' : 'asc');
        setSortColumn(column);
        setPage(0);
    };

    const handleStatusChange = (id, newStatus) => {
        setStatusTemp(prev => ({ ...prev, [id]: newStatus }));
        setSelectedId(id);
        setDialogOpen(true);
    };

    const handleConfirmUpdate = async () => {
        if (selectedId) {
            try {
                await updateDoc(doc(db, 'ouvidoriarh', selectedId), { status: statusTemp[selectedId] });
                setDados(prev =>
                    prev.map(d => d.id === selectedId ? { ...d, status: statusTemp[selectedId] } : d)
                );
                setDialogOpen(false);
                setSelectedId(null);
            } catch (error) {
                console.error('Erro ao atualizar status:', error);
            }
        }
    };

    const exportToExcel = () => {
        const exportData = sortedAndFiltered.map(({ doc1, doc2, doc3, doc4, doc5, ...rest }) => rest);
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Dados');
        XLSX.writeFile(wb, 'dados_ouvidoria.xlsx');
    };

    const hasActiveFilters = Object.values(filters).some(v => v !== '');

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
                <CircularProgress sx={{ color: BLUE }} />
            </Box>
        );
    }

    const headerCellSx = {
        bgcolor: NAVY,
        color: '#fff',
        fontWeight: 600,
        fontSize: 12,
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        py: 1.5,
        '&:hover': { bgcolor: '#012554' },
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Page header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ color: NAVY, fontWeight: 700, letterSpacing: 0.3 }}>
                        Respostas da Ouvidoria
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#888', mt: 0.5, fontSize: 13 }}>
                        {sortedAndFiltered.length} registro{sortedAndFiltered.length !== 1 ? 's' : ''}
                        {dados.length !== sortedAndFiltered.length && (
                            <span style={{ color: '#bbb' }}> de {dados.length} total</span>
                        )}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={exportToExcel}
                    sx={{
                        bgcolor: GOLD,
                        color: NAVY,
                        fontWeight: 700,
                        fontSize: 13,
                        boxShadow: 'none',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#FDD400', boxShadow: 'none' },
                    }}
                >
                    Exportar Excel
                </Button>
            </Box>

            {/* Filter panel */}
            <Paper
                elevation={0}
                sx={{ p: 2.5, mb: 2.5, border: '1px solid #e8eaf0', borderRadius: 2, bgcolor: '#fafbfd' }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="caption" sx={{ color: NAVY, fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
                        Filtros
                    </Typography>
                    {hasActiveFilters && (
                        <Tooltip title="Limpar todos os filtros">
                            <IconButton
                                size="small"
                                onClick={() => { setFilters(EMPTY_FILTERS); setPage(0); }}
                                sx={{ color: '#aaa', ml: 0.5, '&:hover': { color: BLUE } }}
                            >
                                <FilterListOffIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Pesquisar por nome, e-mail ou Nº"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={filters.search}
                            onChange={handleFilterChange('search')}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 17, color: '#bbb' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: BLUE } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select value={filters.status} label="Status" onChange={handleFilterChange('status')}>
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="Pendente">Pendente</MenuItem>
                                <MenuItem value="Em análise">Em análise</MenuItem>
                                <MenuItem value="Concluído">Concluído</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Tipo</InputLabel>
                            <Select value={filters.tipo} label="Tipo" onChange={handleFilterChange('tipo')}>
                                <MenuItem value="">Todos</MenuItem>
                                {tiposDisponiveis.map(tipo => (
                                    <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            label="Data início"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={filters.dataInicio}
                            onChange={handleFilterChange('dataInicio')}
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            label="Data fim"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={filters.dataFim}
                            onChange={handleFilterChange('dataFim')}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Table */}
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: '1px solid #e8eaf0', borderRadius: 2, overflow: 'hidden' }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {[
                                { id: 'numeroRequisicao', label: 'Nº Req' },
                                { id: 'nome', label: 'Nome' },
                                { id: 'tipo', label: 'Tipo' },
                                { id: 'status', label: 'Status' },
                                { id: 'criacao', label: 'Data' },
                            ].map(col => (
                                <TableCell
                                    key={col.id}
                                    onClick={() => handleSort(col.id)}
                                    sx={headerCellSx}
                                >
                                    {col.label}
                                    <SortIcon column={col.id} sortColumn={sortColumn} sortDirection={sortDirection} />
                                </TableCell>
                            ))}
                            <TableCell sx={{ ...headerCellSx, cursor: 'default', width: 56, '&:hover': { bgcolor: NAVY } }}>
                                &nbsp;
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#bbb', fontSize: 14 }}>
                                    Nenhum registro encontrado
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedData.map((dado, idx) => (
                                <React.Fragment key={dado.id}>
                                    <TableRow
                                        sx={{
                                            bgcolor: idx % 2 === 0 ? '#fff' : '#fafbfd',
                                            '&:hover': { bgcolor: 'rgba(0,62,135,0.05)' },
                                            transition: 'background 0.15s',
                                        }}
                                    >
                                        <TableCell sx={{ fontSize: 12, color: '#888', fontWeight: 600, width: 80 }}>
                                            #{dado.numeroRequisicao || '-'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13, fontWeight: 500, color: NAVY, maxWidth: 200 }}>
                                            {dado.nome || '-'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 12, color: '#555' }}>
                                            {dado.tipo || '-'}
                                        </TableCell>
                                        <TableCell sx={{ width: 140 }}>
                                            <Select
                                                value={statusTemp[dado.id] || dado.status || ''}
                                                onChange={(e) => handleStatusChange(dado.id, e.target.value)}
                                                size="small"
                                                variant="outlined"
                                                renderValue={(val) => <StatusChip status={val} />}
                                                sx={{
                                                    fontSize: 12,
                                                    height: 28,
                                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
                                                }}
                                            >
                                                <MenuItem value="Pendente">Pendente</MenuItem>
                                                <MenuItem value="Em análise">Em análise</MenuItem>
                                                <MenuItem value="Concluído">Concluído</MenuItem>
                                            </Select>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 12, color: '#777', whiteSpace: 'nowrap', width: 100 }}>
                                            {formatDate(dado.criacao)}
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: 56 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => setOpen(prev => ({ ...prev, [dado.id]: !prev[dado.id] }))}
                                                sx={{
                                                    color: open[dado.id] ? GOLD : BLUE,
                                                    bgcolor: open[dado.id] ? 'rgba(253,197,0,0.1)' : 'transparent',
                                                    '&:hover': { bgcolor: 'rgba(0,62,135,0.08)' },
                                                }}
                                            >
                                                {open[dado.id]
                                                    ? <ExpandLessIcon fontSize="small" />
                                                    : <ExpandMoreIcon fontSize="small" />
                                                }
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>

                                    {/* Expanded row */}
                                    <TableRow>
                                        <TableCell
                                            colSpan={6}
                                            sx={{
                                                p: 0,
                                                borderBottom: open[dado.id] ? `1px solid #e8eaf0` : 'none',
                                            }}
                                        >
                                            <Collapse in={open[dado.id]} timeout="auto" unmountOnExit>
                                                <Box
                                                    sx={{
                                                        p: 2.5,
                                                        bgcolor: '#f0f4fa',
                                                        borderLeft: `4px solid ${BLUE}`,
                                                    }}
                                                >
                                                    <Grid container spacing={3}>
                                                        <Grid item xs={12} sm={4}>
                                                            <Typography variant="caption" sx={{ color: '#999', textTransform: 'uppercase', letterSpacing: 1, fontSize: 10 }}>
                                                                E-mail
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ color: NAVY, mt: 0.5, fontSize: 13 }}>
                                                                {dado.email || '-'}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={4}>
                                                            <Typography variant="caption" sx={{ color: '#999', textTransform: 'uppercase', letterSpacing: 1, fontSize: 10 }}>
                                                                Documentos Anexados
                                                            </Typography>
                                                            <Box sx={{ mt: 0.75, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                                {[dado.doc1, dado.doc2, dado.doc3].map((docItem, i) =>
                                                                    docItem ? (
                                                                        <Button
                                                                            key={i}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            onClick={() => { setSelectedDoc(docItem); setModalOpen(true); }}
                                                                            sx={{
                                                                                fontSize: 11,
                                                                                borderColor: BLUE,
                                                                                color: BLUE,
                                                                                py: 0.25,
                                                                                borderRadius: 1.5,
                                                                                '&:hover': { bgcolor: 'rgba(0,62,135,0.06)' },
                                                                            }}
                                                                        >
                                                                            Anexo {i + 1}
                                                                        </Button>
                                                                    ) : null
                                                                )}
                                                                {!dado.doc1 && !dado.doc2 && !dado.doc3 && (
                                                                    <Typography variant="body2" sx={{ color: '#bbb', fontSize: 12 }}>
                                                                        Sem anexos
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={12} sm={4}>
                                                            <Typography variant="caption" sx={{ color: '#999', textTransform: 'uppercase', letterSpacing: 1, fontSize: 10 }}>
                                                                Registro / Descrição
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mt: 0.5, color: '#333', whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.6 }}>
                                                                {dado.registro || '-'}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={sortedAndFiltered.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                labelRowsPerPage="Linhas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                sx={{ borderTop: '1px solid #e8eaf0', color: '#666', fontSize: 13 }}
            />

            {/* Confirm status dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ color: NAVY, fontWeight: 700, pb: 1 }}>
                    Confirmar Atualização
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: 14 }}>
                        Atualizar o status para{' '}
                        <strong style={{ color: NAVY }}>"{statusTemp[selectedId]}"</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setDialogOpen(false)}
                        sx={{ color: '#888', borderRadius: 2 }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmUpdate}
                        variant="contained"
                        sx={{ bgcolor: BLUE, borderRadius: 2, '&:hover': { bgcolor: NAVY }, boxShadow: 'none' }}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Document preview dialog */}
            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ color: NAVY, fontWeight: 700 }}>Visualizar Documento</DialogTitle>
                <DialogContent>
                    {selectedDoc ? (
                        <img src={selectedDoc} alt="Anexo" style={{ width: '100%', objectFit: 'contain', borderRadius: 8 }} />
                    ) : (
                        <Typography sx={{ color: '#bbb', textAlign: 'center', py: 4 }}>
                            Nenhum documento disponível
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setModalOpen(false)}
                        variant="outlined"
                        sx={{ borderColor: BLUE, color: BLUE, borderRadius: 2 }}
                    >
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OuvidoriaRH;
